"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useCart } from "../CartContext";
import { useCurrency } from "../CurrencyContext";
import { formatPrice } from "@/lib/fx";
import ProductPlaceholder from "./ProductPlaceholder";
import { isRealPhoto } from "@/lib/brand-mapping";

interface BrandCartDrawerProps {
  locale: string;
  ui?: {
    cartEmpty?: string;
    cartEuDelivery?: string;
    bag?: string;
    emptyState?: string;
    item?: string;
    items?: string;
    closeX?: string;
    addBegin?: string;
    subtotal?: string;
    redirecting?: string;
    checkout?: string;
    freeOverPromo?: string;
    returnsNote?: string;
  };
}

export default function BrandCartDrawer({ locale, ui }: BrandCartDrawerProps) {
  const { cart, isOpen, closeCart, updateQuantity, totalEur } = useCart();
  const { currency, rates } = useCurrency();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const totalDisplay = formatPrice(totalEur, currency, rates);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeCart();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [isOpen, closeCart]);

  async function handleCheckout() {
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const items = cart.items.map((item) => ({
        sku: item.sku,
        name: item.shortName || item.name,
        priceEUR: item.priceEUR,
        qty: item.quantity,
      }));
      // Route by currency: TRY → PayTR (Turkish gateway, supports taksit);
      // EUR/USD → Stripe (existing international flow).
      const endpoint = currency === "TRY" ? "/api/checkout/paytr" : "/api/checkout";
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items, locale, currency }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setCheckoutError(data.error ?? "Checkout failed. Please try again.");
        setCheckoutLoading(false);
        return;
      }
      if (data.url) window.location.href = data.url;
    } catch {
      setCheckoutError("Network error. Please check your connection and try again.");
      setCheckoutLoading(false);
    }
  }

  const items = cart.items;

  return (
    <>
      <div
        onClick={closeCart}
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          background: "oklch(0.10 0 0 / 0.4)",
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? "auto" : "none",
          transition: "opacity 300ms",
          zIndex: 100,
        }}
      />
      <aside
        aria-label="Bag"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(460px, 100vw)",
          background: "var(--bp-bg)",
          borderLeft: "1px solid var(--bp-rule)",
          transform: isOpen ? "translateX(0)" : "translateX(100%)",
          transition: "transform 360ms cubic-bezier(.2,.7,.2,1)",
          zIndex: 101,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid var(--bp-rule)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <span className="bp-mono-sm">{ui?.bag ?? "BAG"}</span>
            <div
              style={{
                fontFamily: "var(--bp-serif)",
                fontSize: 26,
                fontWeight: 300,
                marginTop: 2,
              }}
            >
              {items.length === 0
                ? (ui?.emptyState ?? "Empty")
                : `${items.length} ${items.length > 1 ? (ui?.items ?? "items") : (ui?.item ?? "item")}`}
            </div>
          </div>
          <button
            onClick={closeCart}
            className="bp-mono-sm"
            style={{ color: "var(--bp-ink)", background: "none", border: 0, cursor: "pointer" }}
          >
            {ui?.closeX ?? "CLOSE ×"}
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "0 24px" }}>
          {items.length === 0 ? (
            <div style={{ padding: "64px 0", textAlign: "center", color: "var(--bp-ink-3)" }}>
              <div className="bp-mono-sm">{ui?.cartEmpty ?? "NOTHING HERE YET"}</div>
              <p
                style={{
                  marginTop: 20,
                  fontFamily: "var(--bp-serif)",
                  fontSize: 22,
                  fontWeight: 300,
                }}
              >
                {ui?.addBegin ?? "Add a formulation to begin."}
              </p>
            </div>
          ) : (
            items.map((it) => (
              <div
                key={it.sku}
                style={{
                  display: "grid",
                  gridTemplateColumns: "72px 1fr auto",
                  gap: 16,
                  padding: "22px 0",
                  borderBottom: "1px solid var(--bp-rule)",
                  alignItems: "start",
                }}
              >
                <div
                  style={{
                    background: "var(--bp-bg-2)",
                    padding: 8,
                    width: 72,
                    height: 88,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {it.image && isRealPhoto(it.image) ? (
                    <Image
                      src={it.image}
                      alt={it.name}
                      fill
                      sizes="72px"
                      style={{ objectFit: "cover" }}
                    />
                  ) : (
                    <ProductPlaceholder label={`BP · ${it.sku.slice(-2)}`} compact />
                  )}
                </div>
                <div>
                  <div className="bp-mono-sm">SKU·{it.sku}</div>
                  <div
                    style={{
                      fontFamily: "var(--bp-serif)",
                      fontSize: 18,
                      fontWeight: 300,
                      marginTop: 2,
                      lineHeight: 1.15,
                    }}
                  >
                    {it.name}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginTop: 12,
                      border: "1px solid var(--bp-rule)",
                      width: "fit-content",
                    }}
                  >
                    <button
                      onClick={() => updateQuantity(it.sku, it.quantity - 1)}
                      style={{
                        padding: "6px 12px",
                        fontFamily: "var(--bp-mono)",
                        background: "none",
                        border: 0,
                        cursor: "pointer",
                      }}
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>
                    <span style={{ fontFamily: "var(--bp-mono)", fontSize: 12 }}>
                      {String(it.quantity).padStart(2, "0")}
                    </span>
                    <button
                      onClick={() => updateQuantity(it.sku, it.quantity + 1)}
                      style={{
                        padding: "6px 12px",
                        fontFamily: "var(--bp-mono)",
                        background: "none",
                        border: 0,
                        cursor: "pointer",
                      }}
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div style={{ fontFamily: "var(--bp-mono)", fontSize: 13 }}>
                  {formatPrice(it.priceEUR * it.quantity, currency, rates)}
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div style={{ borderTop: "1px solid var(--bp-rule)", padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span className="bp-mono-sm">{ui?.subtotal ?? "SUBTOTAL"}</span>
              <span style={{ fontFamily: "var(--bp-mono)", fontSize: 13 }}>
                {totalDisplay}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <span className="bp-mono-sm">{ui?.cartEuDelivery ?? "EU DELIVERY"}</span>
              <span style={{ fontFamily: "var(--bp-mono)", fontSize: 13 }}>{ui?.freeOverPromo ?? "FREE OVER €60"}</span>
            </div>
            <button
              onClick={handleCheckout}
              disabled={checkoutLoading}
              className="bp-btn"
              style={{ width: "100%", justifyContent: "center" }}
            >
              {checkoutLoading
                ? (ui?.redirecting ?? "REDIRECTING…")
                : `${ui?.checkout ?? "CHECKOUT"} — ${totalDisplay} →`}
            </button>
            {checkoutError && (
              <p
                className="bp-mono-sm"
                style={{
                  color: "oklch(0.55 0.18 25)",
                  marginTop: 12,
                  textAlign: "center",
                }}
              >
                {checkoutError}
              </p>
            )}
            <p className="bp-mono-sm" style={{ marginTop: 14, textAlign: "center" }}>
              {ui?.returnsNote ?? "30-DAY RETURNS · FREE SAMPLES WITH EVERY ORDER"}
            </p>
          </div>
        )}
      </aside>
    </>
  );
}
